import React from 'react';
import { Box } from '@mui/material';

const ReportSkeleton: React.FC = () => {
  return (
    <Box sx={(theme) => ({ 
      p: 3, 
      backgroundColor: theme.palette.background.default, 
      minHeight: '100vh' 
    })}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={(theme) => ({ 
          width: 40, 
          height: 40, 
          borderRadius: '10px', 
          background: theme.customPalette.surface.card,
          backdropFilter: 'blur(10px)',
        })} />
        <Box sx={(theme) => ({ 
          width: 200, 
          height: 32, 
          background: theme.customPalette.surface.card,
          backdropFilter: 'blur(10px)',
          borderRadius: '6px' 
        })} />
        <Box sx={{ ml: 'auto' }}>
          <Box sx={(theme) => ({ 
            width: 160, 
            height: 40, 
            background: theme.customPalette.surface.card,
            backdropFilter: 'blur(10px)',
            borderRadius: '8px',
            boxShadow: `0 0 10px ${theme.customPalette.glow.primary}`
          })} />
        </Box>
      </Box>

      {/* Project Info Card */}
      <Box sx={(theme) => ({ 
        p: 3, 
        background: theme.customPalette.surface.drawer,
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        mb: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        border: `1px solid ${theme.customPalette.border.subtle}`,
      })}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={(theme) => ({ 
            width: '30%', 
            height: 24, 
            background: theme.customPalette.surface.card,
            borderRadius: '4px' 
          })} />
          <Box sx={(theme) => ({ 
            width: '20%', 
            height: 24, 
            background: theme.customPalette.surface.card,
            borderRadius: '4px' 
          })} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={(theme) => ({ 
            width: '40%', 
            height: 24, 
            background: theme.customPalette.surface.card,
            borderRadius: '4px' 
          })} />
          <Box sx={(theme) => ({ 
            width: '15%', 
            height: 24, 
            background: theme.customPalette.surface.card,
            borderRadius: '4px' 
          })} />
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: 3,
        mb: 4,
      }}>
        {[...Array(5)].map((_, index) => (
          <Box key={index} sx={(theme) => ({
            background: theme.customPalette.surface.drawer,
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            p: 3,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            border: `1px solid ${theme.customPalette.border.subtle}`,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: `0 0 20px ${theme.customPalette.glow.primary}`,
            }
          })}>
            <Box sx={(theme) => ({ 
              width: 40, 
              height: 40, 
              borderRadius: '8px',
              background: theme.customPalette.surface.card,
            })} />
            <Box sx={{ flex: 1 }}>
              <Box sx={(theme) => ({ 
                width: '60%', 
                height: 24, 
                background: theme.customPalette.surface.card,
                borderRadius: '4px',
                mb: 1
              })} />
              <Box sx={(theme) => ({ 
                width: '40%', 
                height: 16, 
                background: theme.customPalette.surface.card,
                borderRadius: '4px'
              })} />
            </Box>
          </Box>
        ))}
      </Box>

      {/* Scan History Report */}
      <Box sx={(theme) => ({ 
        background: theme.customPalette.surface.drawer,
        backdropFilter: 'blur(10px)',
        borderRadius: '12px', 
        p: 3,
        border: `1px solid ${theme.customPalette.border.subtle}`,
      })}>
        <Box sx={(theme) => ({ 
          mb: 3, 
          width: '20%', 
          height: 24, 
          background: theme.customPalette.surface.card,
          borderRadius: '4px' 
        })} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...Array(5)].map((_, index) => (
            <Box key={index} sx={(theme) => ({
              p: 3,
              borderRadius: '8px',
              border: `1px solid ${theme.customPalette.border.subtle}`,
              background: theme.customPalette.surface.card,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: `0 0 20px ${theme.customPalette.glow.primary}`,
              }
            })}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                <Box sx={(theme) => ({ 
                  width: '40%', 
                  height: 20, 
                  background: theme.customPalette.surface.drawer,
                  borderRadius: '4px' 
                })} />
                <Box sx={(theme) => ({ 
                  width: '60%', 
                  height: 16, 
                  background: theme.customPalette.surface.drawer,
                  borderRadius: '4px' 
                })} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={(theme) => ({ 
                  width: 80, 
                  height: 32, 
                  background: theme.customPalette.surface.drawer,
                  borderRadius: '4px' 
                })} />
                <Box sx={(theme) => ({ 
                  width: 80, 
                  height: 32, 
                  background: theme.customPalette.surface.drawer,
                  borderRadius: '4px' 
                })} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ReportSkeleton;
